import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServicesAPI, clustersAPI } from '../../services/api';
import ImageSelector from '../../components/ImageSelector';
import { useAuth } from '../../context/AuthContext';

const PRESETS = [
  { id:'nano',   label:'Nano',   cpu_req:'0.25', cpu_lim:'0.5',  mem_req:0.25, mem_lim:0.5,  desc:'0.25 vCPU · 256MB' },
  { id:'small',  label:'Small',  cpu_req:'0.5',  cpu_lim:'1',    mem_req:0.5,  mem_lim:1,    desc:'0.5 vCPU · 512MB' },
  { id:'medium', label:'Medium', cpu_req:'1',    cpu_lim:'2',    mem_req:1,    mem_lim:2,    desc:'1 vCPU · 1GB' },
  { id:'large',  label:'Large',  cpu_req:'2',    cpu_lim:'4',    mem_req:2,    mem_lim:4,    desc:'2 vCPU · 2GB' },
  { id:'xlarge', label:'XLarge', cpu_req:'4',    cpu_lim:'8',    mem_req:4,    mem_lim:8,    desc:'4 vCPU · 4GB' },
  { id:'custom', label:'Custom', cpu_req:'',     cpu_lim:'',     mem_req:'',   mem_lim:'',   desc:'Custom values' },
];

export default function InstanceLaunchPage() {
  const { hasSectionAction } = useAuth();
  const canCreate = hasSectionAction('workloads', 'create');
  const navigate = useNavigate();
  const fileRef  = useRef();
  const [mode, setMode]     = useState('form'); // form | yaml
  const [yamlContent, setYamlContent] = useState('');
  const [clusters,  setClusters]    = useState([]);
  const [form, setForm] = useState({
    cluster_id:'', name:'', namespace:'compute', image:'',
    use_gpu: false, preset:'medium',
    cpu_request:'1', cpu_limit:'2', mem_request_gb:1, mem_limit_gb:2,
    storage_gb:'', storage_persistent:false, storage_class:'local-storage',
    existing_pvc:'', existing_pvc_mount:'/mnt/data',   // attach an already-created PVC
    command:'', env_vars:'{}',
    // Advanced / Scheduling
    node_selector_key:'', node_selector_val:'',   // single k/v entry (most common: hostname)
    image_pull_policy:'Always',
    image_pull_secret:'',
    toleration_key:'', toleration_effect:'NoSchedule',  // GPU taint toleration
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError]         = useState('');
  const [existingId, setExistingId] = useState(null); // set on a 409 name-exists

  const load = useCallback(async () => {
    try {
      const r = await clustersAPI.list();
      const cl = r.data || [];
      setClusters(cl);
      if (cl.length) setForm(f => ({ ...f, cluster_id: cl[0].id }));
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (id) => {
    const p = PRESETS.find(x => x.id === id);
    if (!p || id === 'custom') { setForm(f => ({...f, preset:id})); return; }
    setForm(f => ({...f, preset:id, cpu_request:p.cpu_req, cpu_limit:p.cpu_lim,
      mem_request_gb:p.mem_req, mem_limit_gb:p.mem_lim}));
  };

  const setF = (k, v) => setForm(f => ({...f, [k]:v}));

  const handleLaunch = async () => {
    if (!form.cluster_id) { setError('Select a cluster'); return; }
    setLaunching(true); setError(''); setExistingId(null);
    try {
      if (mode === 'yaml') {
        if (!yamlContent.trim()) { setError('YAML content is empty'); setLaunching(false); return; }
        const nameM  = yamlContent.match(/^metadata:[\s\S]*?^\s+name:\s*(\S+)/m);
        const nsM    = yamlContent.match(/^metadata:[\s\S]*?^\s+namespace:\s*(\S+)/m);
        const imageM = yamlContent.match(/^\s+image:\s*(\S+)/m);
        const r = await appServicesAPI.create({
          cluster_id: parseInt(form.cluster_id),
          name: nameM?.[1] || `pod-${Date.now()}`,
          display_name: nameM?.[1] || 'Pod',
          image: imageM?.[1] || 'n/a', kind:'Pod',
          namespace: nsM?.[1] || form.namespace || 'compute',
          raw_yaml: yamlContent,
          replicas:1, cpu_request:'1', cpu_limit:'2', mem_request_gb:1, mem_limit_gb:2,
        });
        navigate(`/compute/instances/${r.data.id}`);
        return;
      }
      if (!form.name.trim()) { setError('Instance name is required'); setLaunching(false); return; }
      if (!form.image.trim()) { setError('Container image is required'); setLaunching(false); return; }
      let env_vars = {};
      try { env_vars = JSON.parse(form.env_vars || '{}'); }
      catch { setError('Environment variables: invalid JSON'); setLaunching(false); return; }
      // Build advanced notes
      const nodeSelector = form.node_selector_key
        ? {[form.node_selector_key]: form.node_selector_val} : {};
      const tolerations = form.toleration_key
        ? [{key: form.toleration_key, operator:'Exists', effect: form.toleration_effect||'NoSchedule'}] : [];
      // Attach an EXISTING PVC (already created in Storage → PVCs) as an extra
      // volume + mount. The backend renders extra_volumes[type=pvc] as a
      // persistentVolumeClaim volume and extra_mounts as the volumeMount.
      const existingPvc = (form.existing_pvc || '').trim();
      const extraVols = existingPvc
        ? [{ name: 'existing-pvc', type: 'pvc', source: existingPvc }] : [];
      const extraMounts = existingPvc
        ? [{ name: 'existing-pvc', mountPath: (form.existing_pvc_mount || '/mnt/data').trim() }] : [];
      const notes = JSON.stringify({
        annotations:{}, labels:{'sarv-compute':'true'}, raw_yaml:'',
        ...(form.command ? {command: form.command} : {}),
        image_pull_policy: form.image_pull_policy || 'Always',
        ...(form.image_pull_secret ? {image_pull_secrets:[form.image_pull_secret]} : {}),
        ...(Object.keys(nodeSelector).length ? {node_selector: nodeSelector} : {}),
        ...(tolerations.length ? {tolerations} : {}),
        ...(extraVols.length ? {extra_volumes: extraVols, extra_mounts: extraMounts} : {}),
      });
      const r = await appServicesAPI.create({
        cluster_id:     parseInt(form.cluster_id),
        name:           form.name.toLowerCase().replace(/[^a-z0-9-]/g,'-'),
        display_name:   form.name,
        image:          form.image,
        kind:           'Pod',
        namespace:      form.namespace || 'compute',
        replicas:       1,
        cpu_request:    String(form.cpu_request),
        cpu_limit:      String(form.cpu_limit),
        mem_request_gb: parseFloat(form.mem_request_gb) || 1,
        mem_limit_gb:   parseFloat(form.mem_limit_gb)   || 2,
        storage_gb:     parseInt(form.storage_gb) || null,
        // Persistent volume → send a storage class so the backend provisions a real PVC.
        // Left null for ephemeral emptyDir (legacy behaviour).
        storage_class:  (form.storage_persistent && parseInt(form.storage_gb))
                          ? (form.storage_class || 'local-storage') : null,
        port: null, service_type: null, subdomain: null,
        env_vars, annotations:{}, labels:{'sarv-compute':'true'}, notes,
      });
      navigate(`/compute/instances/${r.data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      // 409 name-exists: detail is a structured object — tell the user this name is
      // already taken in the namespace and offer to open the existing instance,
      // rather than the old silent overwrite/redirect.
      if (e.response?.status === 409 && d && typeof d === 'object' && d.code === 'name_exists') {
        setError(
          `${d.message} To change its image or settings, open the existing instance and use "Update image". ` +
          `To create a separate one, choose a different name.`
        );
        setExistingId(d.existing_id || null);
      } else {
        setError((typeof d === 'string' && d) || 'Launch failed');
      }
    } finally { setLaunching(false); }
  };

  const label = (text, hint) => (
    <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:5 }}>
      {text}{hint && <span style={{ fontWeight:400, color:'var(--muted-2)', marginLeft:6 }}>{hint}</span>}
    </label>
  );

  return (
    <div style={{ padding:'24px 32px', maxWidth:780 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:28 }}>
        <button onClick={() => navigate('/compute/workloads')}
          style={{ background:'none', border:'none', color:'var(--brand)', fontSize:13,
            cursor:'pointer', padding:'4px 0', fontWeight:500 }}>
          ← My Workloads
        </button>
        <span style={{ color:'var(--muted-2)' }}>›</span>
        <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:'var(--ink)' }}>Launch Instance</h1>
      </div>

      {/* Mode toggle */}
      <div className="filter-tabs" style={{ marginBottom:20 }}>
        {[['form','📝 Form'],['yaml','📄 YAML']].map(([id,lbl2])=>(
          <button key={id} onClick={()=>setMode(id)} className={mode===id?'active':undefined}>
            {lbl2}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

        {/* Cluster — always visible */}
        <section className="card">
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18, paddingBottom:10, borderBottom:'1px solid var(--surface-2)' }}>
            Cluster &amp; Namespace
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              {label('Target Cluster','*')}
              <select className="select" value={form.cluster_id} onChange={e=>setF('cluster_id',e.target.value)}>
                <option value="">— Select —</option>
                {clusters.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              {label('Namespace')}
              <input className="input" placeholder="compute" value={form.namespace} onChange={e=>setF('namespace',e.target.value)} />
            </div>
          </div>
        </section>

        {/* ── YAML mode ── */}
        {mode==='yaml' && (
          <section className="card">
            <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18, paddingBottom:10, borderBottom:'1px solid var(--surface-2)' }}>
              Pod YAML Manifest
            </div>
            <input ref={fileRef} type="file" accept=".yaml,.yml"
              onChange={e=>{const f2=e.target.files[0];if(!f2)return;const rd=new FileReader();rd.onload=ev=>setYamlContent(ev.target.result);rd.readAsText(f2);}}
              style={{ fontSize:12, marginBottom:10, display:'block' }} />
            <textarea rows={18}
              placeholder={"apiVersion: v1\nkind: Pod\nmetadata:\n  name: my-pod\n  namespace: compute\nspec:\n  containers:\n  - name: my-pod\n    image: ubuntu:22.04\n    command: [\"sleep\", \"infinity\"]\n    resources:\n      requests:\n        cpu: \"1\"\n        memory: \"1Gi\""}
              value={yamlContent} onChange={e=>setYamlContent(e.target.value)}
              className="textarea" style={{ minHeight:320 }} />
          </section>
        )}

        {/* ── Section 1: Basic ── */}
        {mode==='form' && <></> /* placeholder — sections below are wrapped in {mode==='form' && ...} */}
        {mode==='form' && (
        <section className="card">
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18,
            paddingBottom:10, borderBottom:'1px solid var(--surface-2)' }}>
            1 · Instance Name
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:14 }}>
            <div>
              {label('Instance Name', '*')}
              <input className="input" placeholder="my-instance" value={form.name}
                onChange={e => setF('name', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))} />
              <div style={{ fontSize:11, color:'var(--muted-2)', marginTop:4 }}>Lowercase letters, numbers, hyphens</div>
            </div>
          </div>
        </section>
        )}

        {mode==='form' && <>
        <section className="card">
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18, paddingBottom:10, borderBottom:'1px solid var(--surface-2)' }}>2 · Container Image</div>
          {label('Image','*')}
          <ImageSelector value={form.image} onChange={v=>setF('image',v)} placeholder="ubuntu:22.04, python:3.11, nginx:alpine…" />
          <div style={{ marginTop:16 }}>
            {label('Compute Type')}
            <div style={{ display:'flex', gap:10 }}>
              {[['false','💻 CPU'],['true','⚡ GPU']].map(([val,lbl2])=>(
                <button key={val} onClick={()=>setF('use_gpu',val==='true')}
                  style={{ flex:1, padding:'10px 0', fontSize:13, fontWeight:600, borderRadius:7, cursor:'pointer', border:'1.5px solid',
                    background:String(form.use_gpu)===val?(val==='true'?'var(--accent)':'var(--brand)'):'var(--surface)',
                    color:String(form.use_gpu)===val?'#fff':'var(--muted)',
                    borderColor:String(form.use_gpu)===val?(val==='true'?'var(--accent)':'var(--brand)'):'var(--border)' }}>{lbl2}</button>
              ))}
            </div>
            {form.use_gpu && <div style={{ marginTop:8, padding:'8px 12px', background:'var(--accent-soft)', borderRadius:6, fontSize:12, color:'var(--accent-ink)', border:'1px solid var(--color-accent-100)' }}>GPU pods require NVIDIA device plugin on cluster nodes.</div>}
          </div>
        </section>

        <section className="card">
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18, paddingBottom:10, borderBottom:'1px solid var(--surface-2)' }}>3 · Resources</div>
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            {PRESETS.map(p=>(
              <button key={p.id} onClick={()=>applyPreset(p.id)}
                style={{ padding:'8px 16px', fontSize:12, fontWeight:600, borderRadius:7, cursor:'pointer', border:'1.5px solid',
                  background:form.preset===p.id?'var(--brand)':'var(--surface)', color:form.preset===p.id?'#fff':'var(--ink-2)',
                  borderColor:form.preset===p.id?'var(--brand)':'var(--border)' }}>
                <div>{p.label}</div><div style={{ fontSize:10, fontWeight:400, opacity:0.8, marginTop:1 }}>{p.desc}</div>
              </button>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {[['cpu_request','vCPU Request'],['cpu_limit','vCPU Limit'],['mem_request_gb','RAM Req (GB)'],['mem_limit_gb','RAM Limit (GB)']].map(([k,lbl2])=>(
              <div key={k}>
                {label(lbl2)}
                <input className="input" type="number" step="0.25" min="0.1" value={form[k]}
                  onChange={e=>{setF(k,e.target.value);if(form.preset!=='custom')setF('preset','custom');}}
                  style={{ background: form.preset==='custom' ? '#fff' : 'var(--surface-2)' }} />
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18, paddingBottom:10, borderBottom:'1px solid var(--surface-2)' }}>4 · Storage <span style={{ fontSize:11, fontWeight:400, color:'var(--muted-2)' }}>optional</span></div>
          {label('Storage GB', form.storage_persistent
            ? '(persistent — survives pod restart/recreate)'
            : '(emptyDir — temporary, wiped on pod restart)')}
          <input className="input" type="number" placeholder="50" value={form.storage_gb} onChange={e=>setF('storage_gb',e.target.value)} style={{ maxWidth:200 }} />

          <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:14, cursor:'pointer', fontSize:13, color:'var(--ink)' }}>
            <input type="checkbox" checked={form.storage_persistent}
              disabled={!parseInt(form.storage_gb)}
              onChange={e=>setF('storage_persistent', e.target.checked)} />
            <span>Persistent volume <span style={{ color:'var(--muted-2)', fontWeight:400 }}>(provision a PVC so data survives restarts)</span></span>
          </label>

          {form.storage_persistent && parseInt(form.storage_gb) > 0 && (
            <div style={{ marginTop:14, maxWidth:280 }}>
              {label('StorageClass','(persistent volume backend)')}
              <input className="input" placeholder="local-storage"
                value={form.storage_class} onChange={e=>setF('storage_class', e.target.value)} />
              <div style={{ fontSize:11, color:'var(--muted-2)', marginTop:6 }}>
                A PVC <code>{(form.name||'&lt;name&gt;').toLowerCase().replace(/[^a-z0-9-]/g,'-')}-data</code> is created and mounted at <code>/data</code>. Deleted with the instance.
              </div>
            </div>
          )}

          {/* Attach an EXISTING PVC (created earlier in Storage → PVCs) — mounts
              it into the pod without provisioning a new volume. Survives instance
              deletion (the PVC is not owned by this instance). */}
          <div style={{ marginTop:18, paddingTop:14, borderTop:'1px dashed var(--surface-2)' }}>
            {label('Attach existing PVC', '(optional — mount a pre-created PersistentVolumeClaim)')}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:4 }}>
              <input className="input" placeholder="existing-pvc-name" style={{ flex:'1 1 200px' }}
                value={form.existing_pvc}
                onChange={e=>setF('existing_pvc', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))} />
              <input className="input" placeholder="/mnt/data" style={{ flex:'0 0 160px' }}
                value={form.existing_pvc_mount}
                onChange={e=>setF('existing_pvc_mount', e.target.value)} />
            </div>
            <div style={{ fontSize:11, color:'var(--muted-2)', marginTop:6 }}>
              Name of an existing PVC in the same namespace, and where to mount it. Leave blank to skip.
            </div>
          </div>
        </section>

        <section className="card">
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18, paddingBottom:10, borderBottom:'1px solid var(--surface-2)' }}>5 · Runtime <span style={{ fontSize:11, fontWeight:400, color:'var(--muted-2)' }}>optional</span></div>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              {label('Command Override','(entrypoint for the container)')}
              <input className="input" placeholder="sleep infinity  or  python app.py" value={form.command} onChange={e=>setF('command',e.target.value)} />
              {['ubuntu','debian','centos','alpine','fedora','rockylinux','busybox'].some(b => form.image.toLowerCase().startsWith(b)) && !form.command && (
                <div className="banner warning" style={{ marginTop:6 }}>
                  ⚠ <strong>{form.image.split(':')[0]}</strong> has no default entrypoint — SarvKube auto-adds <code>sleep infinity</code>.
                </div>
              )}
            </div>
            <div>
              {label('Environment Variables','(JSON)')}
              <textarea className="textarea" rows={3} placeholder={'{\n  "KEY": "value"\n}'} value={form.env_vars} onChange={e=>setF('env_vars',e.target.value)} />
            </div>
          </div>
        </section>

        {/* ── Section 6: Scheduling (collapsible) ── */}
        <section style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
          <button onClick={() => setShowAdvanced(v=>!v)}
            style={{ width:'100%', padding:'14px 24px', background:'none', border:'none', cursor:'pointer',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>
              6 · Scheduling &amp; Registry
              <span style={{ fontSize:11, fontWeight:400, color:'var(--muted-2)', marginLeft:8 }}>optional — node selector, tolerations, pull policy</span>
            </span>
            <span style={{ fontSize:16, color:'var(--muted-2)', transition:'transform 0.2s',
              transform: showAdvanced ? 'rotate(180deg)' : 'none' }}>›</span>
          </button>

          {showAdvanced && (
            <div style={{ padding:'0 24px 24px', borderTop:'1px solid var(--surface-2)', display:'flex', flexDirection:'column', gap:16 }}>

              {/* Node Selector */}
              <div>
                {label('Node Selector','(schedule pod on specific node or node label)')}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Label Key</div>
                    <input className="input" placeholder="kubernetes.io/hostname" value={form.node_selector_key}
                      onChange={e=>setF('node_selector_key',e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Label Value</div>
                    <input className="input" placeholder="w1  or  w2" value={form.node_selector_val}
                      onChange={e=>setF('node_selector_val',e.target.value)} />
                  </div>
                </div>
                <div style={{ marginTop:6, fontSize:11, color:'var(--muted-2)' }}>
                  Common keys: <code>kubernetes.io/hostname</code> (specific node) ·
                  <code> node-type</code> · <code>gpu=true</code>
                </div>
              </div>

              {/* Tolerations */}
              <div>
                {label('Toleration','(allow scheduling on tainted nodes, e.g. GPU nodes)')}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Taint Key</div>
                    <input className="input" placeholder="nvidia.com/gpu" value={form.toleration_key}
                      onChange={e=>setF('toleration_key',e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Effect</div>
                    <select className="select" value={form.toleration_effect}
                      onChange={e=>setF('toleration_effect',e.target.value)}>
                      <option value="NoSchedule">NoSchedule</option>
                      <option value="PreferNoSchedule">PreferNoSchedule</option>
                      <option value="NoExecute">NoExecute</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginTop:6, fontSize:11, color:'var(--muted-2)' }}>
                  For GPU nodes use key <code>nvidia.com/gpu</code> with effect <code>NoSchedule</code>
                </div>
              </div>

              {/* Image Pull Policy */}
              <div>
                {label('Image Pull Policy')}
                <div style={{ display:'flex', gap:8 }}>
                  {['Always','IfNotPresent','Never'].map(p=>(
                    <button key={p} onClick={()=>setF('image_pull_policy',p)}
                      style={{ padding:'7px 16px', fontSize:12, fontWeight:600, borderRadius:6, cursor:'pointer', border:'1.5px solid',
                        background: form.image_pull_policy===p ? 'var(--brand)' : 'var(--surface)',
                        color:      form.image_pull_policy===p ? '#fff' : 'var(--ink-2)',
                        borderColor:form.image_pull_policy===p ? 'var(--brand)' : 'var(--border)' }}>
                      {p}
                    </button>
                  ))}
                </div>
                <div style={{ marginTop:5, fontSize:11, color:'var(--muted-2)' }}>
                  <strong>Always</strong> — always pull · <strong>IfNotPresent</strong> — use cached if exists (faster) · <strong>Never</strong> — never pull (local image only)
                </div>
              </div>

              {/* Image Pull Secret */}
              <div>
                {label('Image Pull Secret','(for private registry auth — name of K8s Secret)')}
                <input className="input" placeholder="my-registry-secret" value={form.image_pull_secret}
                  onChange={e=>setF('image_pull_secret',e.target.value)} />
                <div style={{ marginTop:5, fontSize:11, color:'var(--muted-2)' }}>
                  Create the secret first: <code>kubectl create secret docker-registry my-registry-secret --docker-server=... --docker-username=... --docker-password=...</code>
                </div>
              </div>
            </div>
          )}
        </section>
        </>}

        {/* Error & Launch */}
        {error && (
          <div className="banner danger">
            <div>{error}</div>
            {existingId && (
              <button type="button" onClick={() => navigate(`/compute/instances/${existingId}`)}
                className="btn btn-sm" style={{ marginTop: 8 }}>
                Open existing instance →
              </button>
            )}
          </div>
        )}

        <div style={{ display:'flex', gap:12, paddingBottom:40 }}>
          {!canCreate && (
            <span style={{ fontSize:13, color:'#dc2626', alignSelf:'center' }}>
              You do not have permission to create instances. Ask a superadmin to enable it for your organization.
            </span>
          )}
          {canCreate && (
          <button onClick={handleLaunch} disabled={launching} className="btn btn-primary btn-lg">
            {launching ? '⟳ Launching...' : '🚀 Launch Instance'}
          </button>
          )}
          <button onClick={() => navigate('/compute/workloads')} className="btn btn-secondary btn-lg">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
